import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { useShop } from "../context/ShopContext";
import { inr } from "../lib/api";

export const CartDrawer = () => {
  const { cart, cartTotal, cartOpen, setCartOpen, updateQty, removeFromCart } = useShop();
  const navigate = useNavigate();

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent side="right" className="bg-ivory w-full sm:max-w-md p-0 flex flex-col" data-testid="cart-drawer">
        <SheetHeader className="p-6 border-b border-gold/30">
          <SheetTitle className="font-heading text-2xl text-charcoal flex items-center gap-3">
            <ShoppingBag className="w-5 h-5 text-maroon" strokeWidth={1.5} /> Your Bag
          </SheetTitle>
        </SheetHeader>

        {cart.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8 text-center" data-testid="cart-empty">
            <ShoppingBag className="w-10 h-10 text-gold" strokeWidth={1} />
            <p className="text-charcoal/60 text-sm">Your bag is empty.</p>
            <button
              onClick={() => { setCartOpen(false); navigate("/shop"); }}
              data-testid="cart-shop-now-btn"
              className="bg-maroon text-ivory text-xs tracking-[0.15em] uppercase px-8 py-3 hover:bg-maroon-dark transition-colors"
            >
              Shop Sarees
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {cart.map((item) => (
                <div key={item.id} className="flex gap-4" data-testid={`cart-item-${item.id}`}>
                  <Link to={`/product/${item.id}`} onClick={() => setCartOpen(false)}>
                    <img src={item.image} alt={item.name} className="w-20 h-28 object-cover" />
                  </Link>
                  <div className="flex-1">
                    <Link to={`/product/${item.id}`} onClick={() => setCartOpen(false)} className="font-heading text-base text-charcoal hover:text-maroon">
                      {item.name}
                    </Link>
                    <p className="text-sm text-maroon mt-1">{inr(item.price)}</p>
                    <div className="flex items-center gap-3 mt-3">
                      <button onClick={() => updateQty(item.id, item.qty - 1)} data-testid={`cart-qty-minus-${item.id}`} className="w-7 h-7 border border-gold/40 flex items-center justify-center text-charcoal hover:border-gold">
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="text-sm w-4 text-center" data-testid={`cart-qty-${item.id}`}>{item.qty}</span>
                      <button onClick={() => updateQty(item.id, item.qty + 1)} data-testid={`cart-qty-plus-${item.id}`} className="w-7 h-7 border border-gold/40 flex items-center justify-center text-charcoal hover:border-gold">
                        <Plus className="w-3 h-3" />
                      </button>
                      <button onClick={() => removeFromCart(item.id)} data-testid={`cart-remove-${item.id}`} className="ml-auto text-charcoal/40 hover:text-maroon" aria-label="Remove">
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-6 border-t border-gold/30 space-y-4 bg-surface/60">
              <div className="flex justify-between text-sm">
                <span className="text-charcoal/60">Subtotal</span>
                <span className="font-semibold text-charcoal" data-testid="cart-subtotal">{inr(cartTotal)}</span>
              </div>
              <p className="text-xs text-charcoal/50">
                {cartTotal >= 2000 ? "Your order ships free across India." : `Add ${inr(2000 - cartTotal)} more for free shipping.`}
              </p>
              <button
                onClick={() => { setCartOpen(false); navigate("/checkout"); }}
                data-testid="cart-checkout-btn"
                className="w-full bg-maroon text-ivory text-xs tracking-[0.18em] uppercase py-4 hover:bg-maroon-dark transition-colors"
              >
                Proceed to Checkout
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
